import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Scan,
  Droplets,
  MessageSquare,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  CircleDot,
  MapPin,
  Loader2,
  ChevronRight,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Chatbot } from "@/components/Chatbot";
import { goToAppStore, openStylistChat, API } from "../lib/api";

const IMG = {
  barcode: "https://static.prod-images.emergentagent.com/jobs/ca9230b3-f03c-418c-bfeb-5a9295ae9f53/images/05f57a1cabeecc3e413565f80747094c6319434d52fbe688513151fcc1509d5d.jpeg",
  chat: "https://static.prod-images.emergentagent.com/jobs/ca9230b3-f03c-418c-bfeb-5a9295ae9f53/images/c5cd38f320fc03a9076f192eb5515436c2ce5dadbb2ea300081a0f2d219c5122.jpeg",
  map: "https://static.prod-images.emergentagent.com/jobs/ca9230b3-f03c-418c-bfeb-5a9295ae9f53/images/ac5329d19b3797d82c07b91096f4065b885fc8441589272294a73e8214d6f7ff.jpeg",
  cuticle: "https://static.prod-images.emergentagent.com/jobs/ca9230b3-f03c-418c-bfeb-5a9295ae9f53/images/07a7d3f1a1ec4c9f631ea5c713b9870a2e28258d7cc28c8383fbd0a37b9af06a.jpeg",
};

const CYAN = "#00F0FF";
const EMERALD = "#00E5A3";
const AMBER = "#FFB800";

const gridBg = {
  backgroundColor: "#0B0F12",
  backgroundImage:
    "linear-gradient(to right, rgba(0,240,255,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,240,255,0.06) 1px, transparent 1px)",
  backgroundSize: "34px 34px",
};
const gridBgDense = {
  backgroundColor: "#0D1117",
  backgroundImage:
    "linear-gradient(to right, rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.035) 1px, transparent 1px)",
  backgroundSize: "16px 16px",
};

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const glass =
  "bg-[#0D1117]/80 backdrop-blur-md border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)] transition-all duration-300";

const MonoLabel = ({ children, color = CYAN }) => (
  <span
    className="inline-flex items-center gap-2 font-mono text-[0.62rem] sm:text-xs uppercase tracking-[0.24em]"
    style={{ color }}
  >
    <span className="w-1.5 h-1.5 rounded-full hiw-pulse" style={{ background: color, boxShadow: `0 0 10px ${color}` }} />
    {children}
  </span>
);

/* ============ HERO: interactive hair shaft cutaway ============ */
const CUT_LAYERS = {
  cuticle: {
    label: "Cuticle",
    color: CYAN,
    metric: "Scale integrity",
    reading: "Lifted and rough",
    note: "The outer scales lift when heat and hard water strip the surface, so the strand loses shine and snags.",
  },
  lipid: {
    label: "Lipid layer",
    color: EMERALD,
    metric: "Moisture seal",
    reading: "Depleted",
    note: "The protective lipid layer is your strand's natural seal. Once it thins, moisture evaporates faster than it goes in.",
  },
  residue: {
    label: "Surface residue",
    color: AMBER,
    metric: "Mineral load",
    reading: "Building up",
    note: "Dissolved minerals and silicone build a chalky coat on the strand that blocks hydration and dulls colour.",
  },
};

function HairCutaway() {
  const [active, setActive] = useState("cuticle");
  const layer = CUT_LAYERS[active];
  return (
    <div className={"rounded-3xl p-5 sm:p-7 " + glass} style={{ borderColor: layer.color + "40" }} data-testid="hero-cutaway">
      <div className="flex items-center gap-2 flex-wrap">
        {Object.entries(CUT_LAYERS).map(([key, l]) => {
          const on = key === active;
          return (
            <button
              key={key}
              onClick={() => setActive(key)}
              data-testid={`cutaway-toggle-${key}`}
              className="font-mono text-[0.6rem] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full border transition-all duration-300"
              style={{
                color: on ? "#0B0F12" : l.color,
                background: on ? l.color : "transparent",
                borderColor: l.color + (on ? "" : "55"),
                boxShadow: on ? `0 0 16px ${l.color}66` : "none",
              }}
            >
              {l.label}
            </button>
          );
        })}
      </div>

      <div className="relative mt-5 rounded-2xl overflow-hidden" style={gridBgDense}>
        <svg viewBox="0 0 400 240" className="w-full block" role="img" aria-label="Hair shaft cross section">
          <defs>
            <linearGradient id="strand" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#1b2530" />
              <stop offset="1" stopColor="#0f151c" />
            </linearGradient>
          </defs>
          {/* strand body */}
          <rect x="40" y="86" width="320" height="68" rx="34" fill="url(#strand)" stroke="rgba(255,255,255,0.12)" />
          {/* cortex core */}
          <rect x="70" y="104" width="260" height="32" rx="16" fill="rgba(255,255,255,0.05)" />

          {/* cuticle scales */}
          <g style={{ opacity: active === "cuticle" ? 1 : 0.18, transition: "opacity .4s" }}>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <path
                key={i}
                d={`M${60 + i * 40},86 l26,-12 l8,12 Z`}
                fill={active === "cuticle" ? CYAN : "rgba(255,255,255,0.25)"}
                opacity="0.9"
              />
            ))}
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <path
                key={"b" + i}
                d={`M${60 + i * 40},154 l26,12 l8,-12 Z`}
                fill={active === "cuticle" ? CYAN : "rgba(255,255,255,0.25)"}
                opacity="0.9"
              />
            ))}
          </g>

          {/* lipid layer */}
          <g style={{ opacity: active === "lipid" ? 1 : 0.12, transition: "opacity .4s" }}>
            <rect x="46" y="92" width="308" height="8" rx="4" fill={EMERALD} opacity="0.75" />
            <rect x="46" y="140" width="308" height="8" rx="4" fill={EMERALD} opacity="0.75" />
          </g>

          {/* surface residue */}
          <g style={{ opacity: active === "residue" ? 1 : 0.1, transition: "opacity .4s" }}>
            {Array.from({ length: 26 }).map((_, i) => (
              <circle
                key={i}
                cx={70 + (i % 13) * 20}
                cy={i < 13 ? 90 : 150}
                r={2.4}
                fill={AMBER}
                opacity="0.85"
              />
            ))}
          </g>

          {/* scanning line */}
          <line x1="40" y1="70" x2="360" y2="70" stroke={layer.color} strokeWidth="1" opacity="0.5" />
        </svg>
        <div className="absolute inset-x-0 top-0 h-full pointer-events-none overflow-hidden">
          <div
            className="hiw-scanline absolute inset-x-6 h-16 blur-md"
            style={{ background: `linear-gradient(to bottom, transparent, ${layer.color}22, transparent)` }}
          />
        </div>
      </div>

      <motion.div key={active} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="mt-5">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[0.6rem] uppercase tracking-[0.16em] text-slate-400">{layer.metric}</span>
          <span className="font-mono text-sm font-semibold" style={{ color: layer.color }}>{layer.reading}</span>
        </div>
        <p className="mt-2 text-sm text-slate-300 leading-relaxed">{layer.note}</p>
      </motion.div>
    </div>
  );
}

/* ============ PHASE 1: interactive intake phone ============ */
const OIL_OPTS = [
  { key: "dry", label: "Dry", band: "Needs lipid support" },
  { key: "balanced", label: "Balanced", band: "Maintain and protect" },
  { key: "oily", label: "Oily", band: "Clarify and rebalance" },
];

function IntakePhone() {
  const [density, setDensity] = useState(62);
  const [oil, setOil] = useState("balanced");
  const score = Math.round(40 + density * 0.4 + (oil === "balanced" ? 18 : oil === "dry" ? 8 : 6));
  const oilBand = OIL_OPTS.find((o) => o.key === oil).band;
  return (
    <div className="relative mx-auto w-full max-w-[300px]">
      <div className="rounded-[2.4rem] border-[10px] border-[#1b2530] bg-[#0D1117] shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden" data-testid="intake-phone">
        <div className="relative px-5 pt-6 pb-7" style={gridBgDense}>
          <div className="flex items-center justify-between">
            <span className="font-mono text-[0.58rem] uppercase tracking-[0.18em] text-slate-400">Intake</span>
            <span className="luminous font-mono text-[0.58rem] px-2 py-0.5 rounded-full border" style={{ color: CYAN, borderColor: CYAN + "40", background: CYAN + "12" }}>
              Step 1 of 15
            </span>
          </div>
          <h4 className="mt-4 font-display text-lg text-white font-semibold leading-snug">How dense is your hair?</h4>
          <p className="mt-1 text-xs text-slate-400">Drag to match how much hair covers your scalp.</p>

          <input
            type="range"
            min="0"
            max="100"
            value={density}
            onChange={(e) => setDensity(Number(e.target.value))}
            data-testid="intake-density-slider"
            className="mt-4 w-full accent-[#00F0FF] cursor-pointer"
          />
          <div className="flex justify-between font-mono text-[0.55rem] uppercase tracking-[0.14em] text-slate-500">
            <span>Fine</span>
            <span style={{ color: CYAN }}>{density}%</span>
            <span>Thick</span>
          </div>

          <h4 className="mt-6 font-display text-lg text-white font-semibold leading-snug">Scalp by mid-week?</h4>
          <div className="mt-3 flex gap-2">
            {OIL_OPTS.map((o) => {
              const on = o.key === oil;
              return (
                <button
                  key={o.key}
                  onClick={() => setOil(o.key)}
                  data-testid={`intake-oil-${o.key}`}
                  className="flex-1 font-mono text-[0.6rem] uppercase tracking-[0.12em] py-2 rounded-lg border transition-all duration-300"
                  style={{
                    color: on ? "#0B0F12" : "#94A3B8",
                    background: on ? EMERALD : "transparent",
                    borderColor: on ? EMERALD : "rgba(255,255,255,0.12)",
                  }}
                >
                  {o.label}
                </button>
              );
            })}
          </div>

          <div className="mt-6 rounded-xl p-4 border" style={{ borderColor: CYAN + "33", background: CYAN + "0c" }} data-testid="intake-score">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[0.56rem] uppercase tracking-[0.16em] text-slate-400">Baseline confidence</span>
              <span className="font-mono text-base font-bold" style={{ color: CYAN }}>{score}%</span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <motion.div className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${EMERALD}, ${CYAN})` }} animate={{ width: `${score}%` }} transition={{ duration: 0.4 }} />
            </div>
            <p className="mt-2 text-[0.68rem] text-slate-400">{oilBand}</p>
          </div>
        </div>
      </div>
      <div className="absolute -inset-6 -z-10 blur-3xl opacity-40 rounded-full" style={{ background: `radial-gradient(circle at 50% 30%, ${CYAN}33, transparent 70%)` }} />
    </div>
  );
}

/* ============ PHASE 2B: interactive postcode water check ============ */
const BAND_COLOR = { soft: CYAN, moderate: EMERALD, hard: AMBER, very_hard: "#FF8A3D" };

function WaterCheck() {
  const [pc, setPc] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");

  const run = async (e) => {
    e.preventDefault();
    if (!pc.trim()) return;
    setState("loading"); setMsg("");
    try {
      const r = await fetch(`${API}/water-hardness?postcode=${encodeURIComponent(pc.trim())}`);
      const d = await r.json();
      if (!r.ok) { setState("error"); setMsg(typeof d.detail === "string" ? d.detail : "Please try again."); return; }
      setData(d); setState("done");
    } catch {
      setState("error"); setMsg("The lookup is unavailable right now. Please try again shortly.");
    }
  };

  const accent = data ? (BAND_COLOR[data.band] || EMERALD) : CYAN;
  return (
    <div className={"rounded-2xl p-5 sm:p-6 " + glass} data-testid="water-check">
      <form onSubmit={run} className="flex gap-2">
        <div className="relative flex-1">
          <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={pc}
            onChange={(e) => setPc(e.target.value)}
            placeholder="Enter your UK postcode"
            data-testid="water-check-postcode-input"
            className="w-full bg-[#0B0F12] border border-white/12 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00F0FF]/60"
          />
        </div>
        <button
          type="submit"
          data-testid="water-check-submit-btn"
          className="shrink-0 rounded-lg px-4 py-2.5 font-mono text-[0.62rem] uppercase tracking-[0.14em] font-semibold text-[#0B0F12] transition-all duration-300"
          style={{ background: CYAN, boxShadow: `0 0 18px ${CYAN}55` }}
        >
          {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check"}
        </button>
      </form>

      {state === "error" && <p className="mt-3 text-xs text-rose-400" data-testid="water-check-error">{msg}</p>}

      {state === "done" && data && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-xl p-4 border" style={{ borderColor: accent + "40", background: accent + "0d" }} data-testid="water-check-result">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[0.56rem] uppercase tracking-[0.16em] text-slate-400">{data.area}</span>
            <span className="font-mono text-sm font-bold" style={{ color: accent }}>{data.label}</span>
          </div>
          <p className="mt-2 text-xs text-slate-300 leading-relaxed">{data.description}</p>
        </motion.div>
      )}
      {state === "idle" && <p className="mt-3 text-[0.68rem] text-slate-500">Directional result based on your region, used to guide your stylist.</p>}
    </div>
  );
}

/* ============ PHASE 4: interactive calibration loop ============ */
const LOOP = [
  { t: "Day 1", title: "Baseline Intake", note: "Baseline data, barcode scans, and first photos captured under standard lighting." },
  { t: "Days 1 to 29", title: "Monthly Plan", note: "You run the tailored stylist plan: wash intervals, active ingredients, and water neutralising techniques." },
  { t: "Ongoing", title: "Habit Shift", note: "Small, repeatable routine changes that your scalp and strands can actually adapt to." },
  { t: "Day 30", title: "30-Day Review", note: "Upload calibrated progress photos under the same standard lighting to measure real change." },
  { t: "Day 30+", title: "Calibration", note: "Follow up with your stylist to read cuticle recovery, adjust for the season, and set the next 30-day plan." },
];

function CalibrationLoop() {
  const [active, setActive] = useState(0);
  const R = 120;
  const cx = 150, cy = 150;
  return (
    <div className="grid lg:grid-cols-2 gap-8 items-center">
      <div className="relative mx-auto w-[300px] h-[300px]">
        <div className="absolute inset-0 hiw-spin rounded-full border border-dashed border-white/10 pointer-events-none" />
        <svg viewBox="0 0 300 300" className="w-full h-full">
          <circle cx={cx} cy={cy} r={R} fill="none" stroke="rgba(0,240,255,0.14)" strokeWidth="1.5" />
          {LOOP.map((n, i) => {
            const ang = (i / LOOP.length) * Math.PI * 2 - Math.PI / 2;
            const x = cx + R * Math.cos(ang);
            const y = cy + R * Math.sin(ang);
            const on = i === active;
            return (
              <g key={i} onClick={() => setActive(i)} style={{ cursor: "pointer" }} data-testid={`calibration-loop-node-${i + 1}`}>
                <circle cx={x} cy={y} r={on ? 15 : 10} fill={on ? CYAN : "#12181F"} stroke={on ? CYAN : "rgba(255,255,255,0.2)"} strokeWidth="1.5" style={{ filter: on ? `drop-shadow(0 0 10px ${CYAN})` : "none", transition: "all .3s" }} />
                <text x={x} y={y + 4} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="11" fontWeight="700" fill={on ? "#0B0F12" : "#94A3B8"}>{i + 1}</text>
              </g>
            );
          })}
          <RefreshCwIconCenter cx={cx} cy={cy} />
        </svg>
      </div>
      <motion.div key={active} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className={"rounded-2xl p-6 " + glass} style={{ borderColor: CYAN + "33" }}>
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: CYAN }}>{LOOP[active].t}</span>
        <h4 className="mt-2 font-display text-2xl text-white font-bold">{LOOP[active].title}</h4>
        <p className="mt-3 text-sm text-slate-300 leading-relaxed">{LOOP[active].note}</p>
        <div className="mt-5 flex gap-1.5">
          {LOOP.map((_, i) => (
            <button key={i} onClick={() => setActive(i)} data-testid={`calibration-dot-${i + 1}`} className="h-1.5 rounded-full transition-all duration-300" style={{ width: i === active ? 26 : 10, background: i === active ? CYAN : "rgba(255,255,255,0.18)" }} />
          ))}
        </div>
      </motion.div>
    </div>
  );
}

const RefreshCwIconCenter = ({ cx, cy }) => (
  <g transform={`translate(${cx - 14}, ${cy - 14})`} opacity="0.85">
    <foreignObject width="28" height="28">
      <div className="flex items-center justify-center w-7 h-7">
        <RefreshCw className="w-6 h-6" style={{ color: EMERALD }} />
      </div>
    </foreignObject>
  </g>
);

/* ============ small building blocks ============ */
const FlagRow = ({ children }) => (
  <li className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0">
    <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: AMBER + "18" }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: AMBER, boxShadow: `0 0 8px ${AMBER}` }} />
    </span>
    <span className="text-sm text-slate-300">{children}</span>
  </li>
);

const StepCard = ({ n, title, note, color }) => (
  <motion.div {...reveal} className={"relative rounded-2xl p-6 " + glass} style={{ borderColor: color + "2e" }}>
    <span className="font-display text-5xl font-extrabold leading-none" style={{ color: color + "33" }}>{n}</span>
    <h4 className="mt-3 font-display text-lg text-white font-semibold">{title}</h4>
    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{note}</p>
  </motion.div>
);

export default function PrecisionMethod() {
  useEffect(() => {
    document.title = "How It Works | Senior Stylist";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-display text-white selection:bg-[#00F0FF] selection:text-[#0B0F12]" style={{ backgroundColor: "#0B0F12" }}>
      <Navbar />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28" style={gridBg} data-testid="hiw-hero">
        <div className="absolute top-0 right-0 w-[60%] h-[60%] blur-[120px] opacity-30 pointer-events-none" style={{ background: `radial-gradient(circle, ${CYAN}22, transparent 70%)` }} />
        <div className="absolute bottom-0 left-0 w-[50%] h-[50%] blur-[120px] opacity-20 pointer-events-none" style={{ background: `radial-gradient(circle, ${EMERALD}22, transparent 70%)` }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <MonoLabel>The Precision Method</MonoLabel>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Haircare is applied chemistry,{" "}
              <span style={{ color: CYAN }}>not guesswork.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
              Off the shelf products fail because they only treat surface symptoms. We map your hair's total ecosystem,
              from the dissolved minerals in your tap water to the chemical coatings on your strands, paired directly with
              a dedicated professional Senior Stylist.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button
                onClick={goToAppStore}
                data-testid="how-it-works-hero-cta"
                className="group inline-flex items-center justify-center gap-2 rounded-xl px-7 py-4 font-semibold text-[#0B0F12] transition-all duration-300"
                style={{ background: CYAN, boxShadow: `0 0 24px ${CYAN}66` }}
              >
                Start Your Intake Analysis
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <div className="flex items-center gap-2 font-mono text-[0.62rem] uppercase tracking-[0.16em] text-slate-400 self-center">
                <ShieldCheck className="w-4 h-4" style={{ color: EMERALD }} />
                Science first, no guesswork
              </div>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <HairCutaway />
          </motion.div>
        </div>
      </section>

      {/* ===================== PHASE 1 ===================== */}
      <section className="relative py-20 md:py-28" style={gridBgDense} data-testid="hiw-phase-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div {...reveal}>
            <MonoLabel>Phase 01 / Baseline Intake</MonoLabel>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              A 15 question intake that builds your Style DNA.
            </h2>
            <p className="mt-5 text-base text-slate-300 leading-relaxed">
              Your scalp works best at a slightly acidic balance, a pH of 4.5 to 5.5. Excessive heat and chemical
              processing break the disulfide bonds inside each strand and strip the protective lipid layer, so moisture
              escapes faster than it goes in. A short, 15 question intake captures the signals that matter, without the
              overwhelm.
            </p>
            <div className={"mt-7 rounded-2xl p-6 " + glass}>
              <p className="font-mono text-[0.62rem] uppercase tracking-[0.16em]" style={{ color: CYAN }}>
                A few of the signals we read
              </p>
              <div className="mt-4 grid sm:grid-cols-3 gap-x-6 gap-y-3">
                {["Chemical history", "Strand density", "Colour"].map((x) => (
                  <div key={x} className="flex items-center gap-2.5">
                    <CircleDot className="w-3.5 h-3.5 shrink-0" style={{ color: EMERALD }} />
                    <span className="text-sm text-slate-300">{x}</span>
                  </div>
                ))}
              </div>
              <p className="mt-5 pt-4 border-t border-white/8 text-sm text-slate-400 leading-relaxed">
                Every answer feeds your{" "}
                <span className="font-semibold" style={{ color: EMERALD }}>Style DNA matrix</span>, the living profile
                your Senior Stylist works from to tailor each recommendation.
              </p>
            </div>
          </motion.div>
          <motion.div {...reveal}>
            <IntakePhone />
          </motion.div>
        </div>
      </section>

      {/* ===================== PHASE 2 ===================== */}
      <section className="relative py-20 md:py-28" style={gridBg} data-testid="hiw-phase-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="max-w-2xl">
            <MonoLabel>Phase 02 / Product and Water Chemistry</MonoLabel>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              Two silent factors decide your results before you lift a brush.
            </h2>
          </motion.div>

          {/* Sub-module A: Barcode */}
          <motion.div {...reveal} className={"mt-12 rounded-3xl overflow-hidden grid lg:grid-cols-2 " + glass}>
            <div className="p-7 sm:p-10 order-2 lg:order-1">
              <span className="inline-flex items-center gap-2 font-mono text-[0.62rem] uppercase tracking-[0.18em]" style={{ color: CYAN }}>
                <Scan className="w-4 h-4" /> The Barcode Scanner
              </span>
              <h3 className="mt-3 font-display text-xl sm:text-2xl font-semibold">Formulation breakdown</h3>
              <p className="mt-4 text-sm text-slate-300 leading-relaxed">
                Ingredients layer and interact. Insoluble silicones like dimethicone form a sealing barrier that can
                choke out moisture unless the right surfactant clears them. Scanning a barcode pulls the full INCI
                ingredient list, so your stylist can spot what is really happening on the strand.
              </p>
              <ul className="mt-5">
                <FlagRow>Hidden drying alcohols</FlagRow>
                <FlagRow>Protein and moisture imbalance</FlagRow>
                <FlagRow>Silicone build up</FlagRow>
                <FlagRow>Harsh surfactants and oil rebound</FlagRow>
              </ul>
            </div>
            <div className="relative order-1 lg:order-2 min-h-[240px]" style={gridBgDense}>
              <img src={IMG.barcode} alt="Barcode scan of a product with a hair strand showing polymer build up" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </motion.div>

          {/* Sub-module B: Water */}
          <motion.div {...reveal} className="mt-8 grid lg:grid-cols-2 gap-8 items-stretch">
            <div className={"rounded-3xl p-7 sm:p-10 flex flex-col " + glass} style={{ borderColor: EMERALD + "26" }}>
              <span className="inline-flex items-center gap-2 font-mono text-[0.62rem] uppercase tracking-[0.18em]" style={{ color: EMERALD }}>
                <Droplets className="w-4 h-4" /> The Postcode Water Check
              </span>
              <h3 className="mt-3 font-display text-xl sm:text-2xl font-semibold">Dissolved minerals</h3>
              <p className="mt-4 text-sm text-slate-300 leading-relaxed">
                UK tap water changes dramatically by postcode. Hard water areas leave dissolved calcium carbonate and
                magnesium that cling to the strand, building a chalky, brittle shield that blocks hydration and dulls
                colour. Soft water stops surfactants rinsing cleanly, so film builds up instead. Your postcode tells your
                stylist whether you need a chelating step or an acidifying rinse.
              </p>
              <div className="mt-6">
                <WaterCheck />
              </div>
            </div>

            <div className="grid gap-4">
              <div className={"rounded-3xl overflow-hidden relative " + glass}>
                <img src={IMG.map} alt="UK water hardness map" className="w-full h-[220px] object-cover" loading="lazy" />
                <span className="absolute bottom-3 left-4 font-mono text-[0.56rem] uppercase tracking-[0.16em] text-slate-300 bg-[#0B0F12]/70 px-2 py-1 rounded">Soft to hard by region</span>
              </div>
              <div className={"rounded-3xl overflow-hidden relative " + glass}>
                <img src={IMG.cuticle} alt="Mineral caked cuticle versus smoothed cuticle" className="w-full h-[200px] object-cover" loading="lazy" />
                <div className="absolute inset-0 flex items-end justify-between p-4 pointer-events-none">
                  <span className="font-mono text-[0.56rem] uppercase tracking-[0.14em] px-2 py-1 rounded" style={{ color: AMBER, background: "#0B0F12aa" }}>Mineral caked</span>
                  <span className="font-mono text-[0.56rem] uppercase tracking-[0.14em] px-2 py-1 rounded" style={{ color: CYAN, background: "#0B0F12aa" }}>Smoothed and sealed</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===================== PHASE 3 ===================== */}
      <section className="relative py-20 md:py-28" style={gridBgDense} data-testid="hiw-phase-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div {...reveal} className="order-2 lg:order-1 relative mx-auto max-w-[320px]">
            <div className="absolute -inset-8 -z-10 blur-3xl opacity-40 rounded-full" style={{ background: `radial-gradient(circle, ${CYAN}2e, transparent 70%)` }} />
            <img src={IMG.chat} alt="Dark mode stylist consultation messaging interface" className="w-full rounded-[2rem] border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.6)]" loading="lazy" />
          </motion.div>
          <div className="order-1 lg:order-2">
            <motion.div {...reveal}>
              <MonoLabel>Phase 03 / Dedicated Stylist Consultation</MonoLabel>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                Pure data is useless without human mastery.
              </h2>
              <p className="mt-5 text-base text-slate-300 leading-relaxed">
                Your numbers are read and applied by a real, vetted professional, not left to a template.
              </p>
            </motion.div>
            <div className="mt-8 grid gap-4">
              <StepCard n="01" color={CYAN} title="Comprehensive Intake Review" note="Your assigned Senior Stylist studies your 15 point baseline, your scanned ingredient roster, and your local water hardness index." />
              <StepCard n="02" color={EMERALD} title="Direct Message Consultation" note="Clear, one to one messaging inside the app to talk through your routine, your lifestyle limits, and your immediate goals." />
              <StepCard n="03" color={CYAN} title="The Tailored 30-Day Recommendation Plan" note="A short, practical monthly roadmap: exact wash intervals, the active ingredients to introduce, the products to bench, and the water neutralising wash techniques to use." />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== PHASE 4 ===================== */}
      <section className="relative py-20 md:py-28" style={gridBg} data-testid="hiw-phase-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="max-w-2xl mb-12">
            <MonoLabel>Phase 04 / The 30-Day Calibration Cycle</MonoLabel>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              Quick fixes fade. Biology compounds.
            </h2>
            <p className="mt-5 text-base text-slate-300 leading-relaxed">
              Your scalp renews its skin cells roughly every 28 days, and hair grows about half an inch a month. We sync
              your plan to that rhythm, so each cycle builds on the last.
            </p>
          </motion.div>
          <CalibrationLoop />
        </div>
      </section>

      {/* ===================== CONVERSION FOOTER ===================== */}
      <section className="relative py-20 md:py-28" style={gridBgDense} data-testid="hiw-conversion">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="relative rounded-[2rem] overflow-hidden border border-white/10 p-10 sm:p-16 text-center" style={{ backgroundColor: "#0B0F12" }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse at 50% 0%, ${CYAN}1f, transparent 60%)` }} />
            <div className="absolute inset-0 opacity-[0.5]" style={gridBg} />
            <div className="relative">
              <Sparkles className="w-7 h-7 mx-auto hiw-pulse" style={{ color: CYAN }} />
              <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight max-w-2xl mx-auto">
                Stop guessing what your hair needs. Let us look at the science.
              </h2>
              <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={goToAppStore}
                  data-testid="footer-primary-cta"
                  className="group inline-flex items-center justify-center gap-2 rounded-xl px-8 py-4 font-semibold text-[#0B0F12] transition-all duration-300"
                  style={{ background: CYAN, boxShadow: `0 0 28px ${CYAN}66` }}
                >
                  Begin Your 15-Question Intake
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={openStylistChat}
                  data-testid="footer-secondary-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-xl px-8 py-4 font-medium text-white border border-white/15 hover:border-[#00F0FF]/60 transition-all duration-300"
                >
                  <MessageSquare className="w-4 h-4" style={{ color: CYAN }} />
                  Speak with our styling concierge
                  <ChevronRight className="w-4 h-4 opacity-60" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <Chatbot />
    </div>
  );
}
